import type { MasterOrderLine } from "~/@types/orderLines";

import { OrderEntryUnit } from "~/gql/graphqlBase.generated";
import { LEGACY_ORDER_ENTRY_UNIT } from "~fulfill/orders/helpers/orderEntryUnitFor";
import {
	eachesToUnits,
	formatEaches,
	formatUnits,
	isEachesEntry,
	unitsToEaches
} from "~fulfill/orders/helpers/orderLineUnits";
import sumWeekQuantities from "~fulfill/orders/helpers/sumWeekQuantities";

export interface MasterLineUnitsSource {
	eachesPerUnit?: null | number;
	shippingUomName?: null | string;
}

const masterEachesEntry = (orderEntryUnit: null | OrderEntryUnit | undefined): boolean =>
	isEachesEntry(orderEntryUnit ?? LEGACY_ORDER_ENTRY_UNIT);

const masterEntryPackSize = (line: MasterLineUnitsSource | null | undefined, eachesEntry: boolean): number => {
	if (eachesEntry) return 1;
	const pack = line?.eachesPerUnit;
	return pack != null && pack > 0 ? pack : 1;
};

const masterEachesToEntry = (
	eaches: unknown,
	line: MasterLineUnitsSource | null | undefined,
	eachesEntry: boolean
): null | number => eachesToUnits(eaches, masterEntryPackSize(line, eachesEntry));

const masterEntryToEaches = (
	typed: unknown,
	line: MasterLineUnitsSource | null | undefined,
	eachesEntry: boolean
): null | number => {
	const eaches = unitsToEaches(typed, masterEntryPackSize(line, eachesEntry));
	return eaches == null ? null : Math.round(eaches);
};

const masterWeekEaches = (line: MasterOrderLine, eachesEntry: boolean): number =>
	sumWeekQuantities(line) * masterEntryPackSize(line, eachesEntry);

const formatMasterEntryQuantity = (entryQuantity: unknown, eachesEntry: boolean): string =>
	eachesEntry ? formatEaches(entryQuantity) : formatUnits(entryQuantity);

const masterQuantityEditToEaches = <T extends { data: MasterLineUnitsSource; newValue: unknown; oldValue: unknown }>(
	edit: T,
	eachesEntry: boolean
): T => {
	const eaches = masterEntryToEaches(edit.newValue, edit.data, eachesEntry);
	return {
		...edit,
		newValue: eaches == null ? edit.newValue : eaches,
		oldValue: masterEntryToEaches(edit.oldValue, edit.data, eachesEntry)
	};
};

const masterOrderUnits = {
	formatMasterEntryQuantity,
	masterEachesEntry,
	masterEachesToEntry,
	masterEntryPackSize,
	masterEntryToEaches,
	masterQuantityEditToEaches,
	masterWeekEaches
};

export default masterOrderUnits;
